import { useEffect, useState } from "react";
import axios from "axios";
import { Brain, Pause, Play, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REALM_META = {
  marketing: { label: "Marketing",  color: "#FF4F27" },
  designer:  { label: "Designing",  color: "#8B5CF6" },
  cgi:       { label: "CGI",        color: "#3B82F6" },
  movie:     { label: "Movie",      color: "#F59E0B" },
};

const SIGNAL_LABEL = {
  style:        "Style",
  music_genre:  "Music vibe",
  genre:        "Genre",
  tone:         "Tone",
  voice:        "Voice",
  polish_mode:  "Polish mode",
  aspect_ratio: "Aspect ratio",
  duration:     "Duration",
  kling_model:  "Kling model",
  length:       "Length",
};

/**
 * XEA Brain panel — shows the user what the brain learned across each
 * realm, plus a Pause / Resume toggle and a "Reset" hard-delete button.
 *
 * Mounted on the Profile page. All actions are best-effort and surface
 * a toast on completion. When the brain has no events yet we render a
 * friendly empty-state instead.
 */
export default function BrainPanel() {
  const { token } = useAuth();
  const [brain, setBrain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const r = await axios.get(`${API}/brain/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBrain(r.data || { total_events: 0, paused: false, per_realm: {} });
    } catch (_e) {
      setBrain({ total_events: 0, paused: false, per_realm: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const togglePause = async () => {
    if (!brain) return;
    setBusy(true);
    const next = !brain.paused;
    try {
      await axios.patch(
        `${API}/brain/pause`,
        { paused: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(next ? "Brain paused — XEA won't learn from new gens" : "Brain resumed — XEA is learning again");
      await load();
    } catch (_e) {
      toast.error("Couldn't update the brain — try again");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset your XEA Brain? All learned preferences will be deleted. Global trends are unaffected.")) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/brain/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Brain wiped — XEA will start learning fresh");
      await load();
    } catch (_e) {
      toast.error("Couldn't reset — try again");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 rounded-2xl border border-ink-200 bg-white flex items-center gap-3" data-testid="brain-panel-loading">
        <Loader2 className="w-5 h-5 animate-spin text-ink-500" />
        <span className="text-sm text-ink-500">Loading your brain…</span>
      </div>
    );
  }

  const totalEvents = brain?.total_events || 0;
  const perRealm = brain?.per_realm || {};
  const hasData = Object.keys(perRealm).length > 0;

  return (
    <div
      className="p-6 rounded-2xl border border-ink-200 bg-white space-y-5"
      data-testid="brain-panel"
      id="brain"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-violet-500 flex items-center justify-center shrink-0">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-ink-950">Your XEA Brain</h3>
            <p className="text-xs text-ink-500 mt-0.5">
              XEA learns from each finished generation so it can pre-fill the picks <em>you</em> usually make.
              {brain?.paused && <span className="ml-1 inline-flex items-center text-amber-600 font-semibold">· Paused</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={togglePause}
            disabled={busy}
            data-testid="brain-pause-toggle"
            className="text-xs"
          >
            {brain?.paused ? (<><Play className="w-3.5 h-3.5 mr-1.5" />Resume</>) : (<><Pause className="w-3.5 h-3.5 mr-1.5" />Pause</>)}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            disabled={busy || totalEvents === 0}
            data-testid="brain-reset-btn"
            className="text-xs text-red-600 hover:bg-red-50 hover:border-red-300"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />Reset
          </Button>
        </div>
      </div>

      {/* Total events stat */}
      <div className="flex items-baseline gap-3">
        <span className="text-3xl font-black font-display text-ink-950" data-testid="brain-total-events">{totalEvents}</span>
        <span className="text-xs uppercase tracking-[0.15em] font-bold text-ink-500">events learned</span>
      </div>

      {!hasData && (
        <div className="rounded-xl border border-dashed border-ink-300 p-5 text-center" data-testid="brain-empty-state">
          <p className="text-sm text-ink-700">
            Your brain is empty — go ship something and XEA will start adapting to your taste.
          </p>
        </div>
      )}

      {hasData && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="brain-realm-grid">
          {Object.entries(perRealm).map(([realm, block]) => {
            const meta = REALM_META[realm] || { label: realm, color: "#737373" };
            const tops = block.tops || {};
            return (
              <div
                key={realm}
                className="rounded-xl border p-4"
                style={{ borderColor: `${meta.color}33`, background: `${meta.color}05` }}
                data-testid={`brain-realm-${realm}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className="text-[10px] uppercase tracking-[0.15em] font-bold"
                    style={{ color: meta.color }}
                  >
                    {meta.label}
                  </span>
                  <span className="text-[10px] text-ink-500">{block.total_events} events</span>
                </div>
                {Object.keys(tops).length === 0 ? (
                  <p className="text-xs text-ink-500">No top picks yet — XEA is still listening.</p>
                ) : (
                  <ul className="space-y-1">
                    {Object.entries(tops).map(([sig, vals]) => (
                      <li key={sig} className="text-xs text-ink-700 flex items-baseline gap-1.5">
                        <span className="text-ink-500 min-w-[80px]">{SIGNAL_LABEL[sig] || sig}:</span>
                        <span className="font-semibold text-ink-950">{vals[0]}</span>
                        {vals.length > 1 && (
                          <span className="text-ink-400">· {vals.slice(1).join(", ")}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-ink-500 leading-relaxed">
        Privacy: this brain lives only on your XEA account. Global trends shown elsewhere are aggregated and never tied back to individual users. See Privacy Policy §2(e).
      </p>
    </div>
  );
}
